import { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  Award,
  Trophy,
  BookOpen,
  GraduationCap,
  FileText,
  AlertCircle,
  History
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { useAdminAuth } from '../../hooks/useAuth';

import { PageTitle } from './shared';
import { getAdminUser } from './navigation/sectionAccess';
import { useRememberedTab } from './navigation/useRememberedTab';

const INSTRUCTOR_ROLES = ['instructor', 'owner'];
const REVIEW_TABS = ['pending', 'approved', 'rejected', 'all'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// Helpers
// ============================================
const getStatusBadge = (status) => {
  const variants = {
    pending: { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300', label: 'Pending' },
    approved: { className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300', label: 'Approved' },
    rejected: { className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300', label: 'Rejected' },
  };
  const variant = variants[status] || variants.pending;
  return <Badge className={variant.className}>{variant.label}</Badge>;
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'prior_experience': return <BookOpen className="h-4 w-4 text-blue-600" />;
    case 'promotion_history': return <Award className="h-4 w-4 text-amber-600" />;
    case 'tournament_history': return <Trophy className="h-4 w-4 text-purple-600" />;
    case 'seminar_history': return <GraduationCap className="h-4 w-4 text-green-600" />;
    default: return <FileText className="h-4 w-4 text-gray-600" />;
  }
};

const getTypeLabel = (type) => {
  const labels = {
    prior_experience: 'Prior Experience',
    promotion_history: 'Promotion History',
    tournament_history: 'Tournament History',
    seminar_history: 'Seminar History'
  };
  return labels[type] || type;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ============================================
// Submission Detail Renderers
// ============================================
const PriorExperienceDetail = ({ entries }) => (
  <div className="space-y-3">
    {entries.map((entry, idx) => (
      <div key={idx} className="p-3 rounded border bg-gray-50 dark:bg-gray-800">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-gray-500">Style:</span> <strong>{entry.style}</strong></div>
          {entry.years_trained != null && <div><span className="text-gray-500">Years:</span> {entry.years_trained}</div>}
          {entry.highest_rank && <div><span className="text-gray-500">Highest Rank:</span> {entry.highest_rank}</div>}
          {entry.school_name && <div><span className="text-gray-500">School:</span> {entry.school_name}</div>}
        </div>
        {entry.notes && <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">{entry.notes}</p>}
      </div>
    ))}
  </div>
);

const PromotionHistoryDetail = ({ entries }) => {
  const beltLabels = {
    white: 'White', yellow: 'Yellow', orange: 'Orange', green: 'Green',
    blue: 'Blue', purple: 'Purple', brown: 'Brown',
    black_1: '1st Dan', black_2: '2nd Dan', black_3: '3rd Dan'
  };
  return (
    <div className="space-y-3">
      {entries.map((entry, idx) => (
        <div key={idx} className="p-3 rounded border bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{beltLabels[entry.from_belt] || entry.from_belt}</span>
            <span className="text-gray-400">&rarr;</span>
            <span className="font-medium">{beltLabels[entry.to_belt] || entry.to_belt}</span>
            {entry.approximate_date && (
              <span className="text-gray-500 ml-2">({new Date(entry.approximate_date).toLocaleDateString()})</span>
            )}
          </div>
          {entry.notes && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 italic">{entry.notes}</p>}
        </div>
      ))}
    </div>
  );
};

const TournamentHistoryDetail = ({ entries }) => {
  const eventLabels = { kata: 'Kata', kumite: 'Kumite', kobudo: 'Kobudo', team_kata: 'Team Kata', other: 'Other' };
  const placeLabels = { '1': '1st Place', '2': '2nd Place', '3': '3rd Place', participated: 'Participated' };

  return (
    <div className="space-y-3">
      {entries.map((entry, idx) => (
        <div key={idx} className="p-3 rounded border bg-gray-50 dark:bg-gray-800">
          <div className="text-sm">
            <p className="font-medium">{entry.name}</p>
            <div className="flex gap-3 text-gray-500 text-xs mt-1">
              {entry.approximate_date && <span>{new Date(entry.approximate_date).toLocaleDateString()}</span>}
              {entry.location && <span>{entry.location}</span>}
            </div>
          </div>
          {entry.events && entry.events.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {entry.events.map((ev, eIdx) => (
                <span key={eIdx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white dark:bg-gray-700 rounded text-xs border">
                  {eventLabels[ev.event_type] || ev.event_type}
                  {ev.division && <span className="text-gray-400">({ev.division})</span>}
                  <span className="text-gray-500">&mdash;</span>
                  {placeLabels[ev.place] || ev.place}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const SeminarHistoryDetail = ({ entries }) => (
  <div className="space-y-3">
    {entries.map((entry, idx) => (
      <div key={idx} className="p-3 rounded border bg-gray-50 dark:bg-gray-800">
        <div className="text-sm">
          <p className="font-medium">{entry.seminar_name}</p>
          <div className="flex flex-wrap gap-3 text-gray-500 text-xs mt-1">
            {entry.seminar_date && <span>{new Date(entry.seminar_date).toLocaleDateString()}</span>}
            {entry.instructor && <span>Instructor: {entry.instructor}</span>}
            {entry.host_dojo && <span>Host: {entry.host_dojo}</span>}
            {entry.hours != null && <span>{entry.hours} hr{entry.hours === 1 ? '' : 's'}</span>}
          </div>
        </div>
        {entry.topics && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            <span className="text-gray-500">Topics:</span> {entry.topics}
          </p>
        )}
        {entry.certificate_url && (
          <p className="text-sm mt-1">
            <a href={entry.certificate_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              View certificate
            </a>
          </p>
        )}
        {entry.notes && <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">{entry.notes}</p>}
      </div>
    ))}
  </div>
);

const SubmissionDetailContent = ({ submission }) => {
  if (!submission) return null;
  const data = submission[submission.submission_type];
  if (!data) return <p className="text-sm text-gray-500">No data available. Click View to load details.</p>;
  switch (submission.submission_type) {
    case 'prior_experience': return <PriorExperienceDetail entries={data} />;
    case 'promotion_history': return <PromotionHistoryDetail entries={data} />;
    case 'tournament_history': return <TournamentHistoryDetail entries={data} />;
    case 'seminar_history': return <SeminarHistoryDetail entries={data} />;
    default: return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>;
  }
};

// ============================================
// Main Component
// ============================================
const HistorySubmissionReview = () => {
  const { token } = useAdminAuth();
  // Defense in depth: the admin nav already hides this page for non-instructors
  // and the API will 403, but if someone deep-links in we show a friendly banner
  // instead of a blank screen of failed fetches.
  const adminUser = getAdminUser();
  const hasInstructorRole = INSTRUCTOR_ROLES.includes(adminUser?.role);

  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useRememberedTab(
    'historyreviews.status-tab', REVIEW_TABS, 'pending',
  );
  const [typeFilter, setTypeFilter] = useState('');

  // Detail modal
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Approve dialog
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvingSubmission, setApprovingSubmission] = useState(null);

  // Reject dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.set('status', activeTab);
      if (typeFilter) params.set('submission_type', typeFilter);

      const [subsResponse, statsResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/history-submissions?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/history-submissions/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!subsResponse.ok) throw new Error('Failed to load submissions');

      const subsData = await subsResponse.json();
      setSubmissions(subsData);

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Failed to load submissions:', err);
      setError('Failed to load history submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, typeFilter]);

  useEffect(() => {
    // Skip the admin fetches when role-gated — the API would 403 anyway, and
    // firing them causes a misleading error flash on a page the user shouldn't
    // be seeing data on.
    if (!hasInstructorRole) return;
    loadData();
  }, [loadData, hasInstructorRole]);

  const handleViewDetails = async (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);

    // Fetch full detail if data not already loaded
    if (!submission[submission.submission_type]) {
      setDetailLoading(true);
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/admin/history-submissions/${submission.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setSelectedSubmission(data);
        }
      } catch (err) {
        console.error('Failed to fetch detail:', err);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  const handleApprove = (submission) => {
    setApprovingSubmission(submission);
    setShowApproveDialog(true);
  };

  const confirmApprove = async () => {
    if (!approvingSubmission) return;

    setProcessingAction(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/history-submissions/${approvingSubmission.id}/approve`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        alert(data.detail || 'Failed to approve submission');
        return;
      }

      setShowApproveDialog(false);
      setShowDetailModal(false);
      setApprovingSubmission(null);
      loadData();
    } catch (err) {
      console.error('Approval error:', err);
      alert('An error occurred while approving');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = (submission) => {
    setSelectedSubmission(submission);
    setReviewerNotes('');
    setShowRejectDialog(true);
  };

  const submitRejection = async () => {
    if (!reviewerNotes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setProcessingAction(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/history-submissions/${selectedSubmission.id}/reject`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewer_notes: reviewerNotes }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        alert(data.detail || 'Failed to reject submission');
        return;
      }

      setShowRejectDialog(false);
      setShowDetailModal(false);
      loadData();
    } catch (err) {
      console.error('Rejection error:', err);
      alert('An error occurred while rejecting');
    } finally {
      setProcessingAction(false);
    }
  };

  if (!hasInstructorRole) {
    return (
      <div className="space-y-6">
        <PageTitle
          title="History Submissions"
          description="Review student-submitted martial arts history"
          icon={<History className="h-6 w-6" />}
        />
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Instructor access required. Approving or rejecting student history
            applies to a student&apos;s permanent record, so this section is
            limited to instructors. If you should have instructor access, ask a
            protected admin to update your role.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="History Submissions"
        description="Review student-submitted martial arts history"
        icon={<History className="h-6 w-6" />}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Type filter */}
      <div className="flex items-center gap-2">
        <Label className="text-sm">Filter by type:</Label>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
        >
          <option value="">All Types</option>
          <option value="prior_experience">Prior Experience</option>
          <option value="promotion_history">Promotion History</option>
          <option value="tournament_history">Tournament History</option>
          <option value="seminar_history">Seminar History</option>
        </select>
      </div>

      {/* Status tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending">
            <Clock className="w-4 h-4 mr-1" />
            Pending
            {stats.pending > 0 && <Badge className="ml-2 bg-amber-500">{stats.pending}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle className="w-4 h-4 mr-1" />
            Approved
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <XCircle className="w-4 h-4 mr-1" />
            Rejected
          </TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
                No submissions found
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <Card key={submission.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          {getTypeIcon(submission.submission_type)}
                          <h3 className="font-semibold">{submission.student_name || 'Unknown Student'}</h3>
                          {getStatusBadge(submission.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <span>{getTypeLabel(submission.submission_type)}</span>
                          <span>{submission.entry_count || 0} {(submission.entry_count || 0) === 1 ? 'entry' : 'entries'}</span>
                          <span>{formatDate(submission.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => handleViewDetails(submission)}>
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        {submission.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(submission)}
                              disabled={processingAction}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleReject(submission)}
                              disabled={processingAction}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedSubmission && getTypeIcon(selectedSubmission.submission_type)}
              {selectedSubmission && getTypeLabel(selectedSubmission.submission_type)} Submission
            </DialogTitle>
            <DialogDescription>
              From: {selectedSubmission?.student_name || 'Unknown Student'} &mdash; {formatDate(selectedSubmission?.created_at)}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : selectedSubmission && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <div className="mt-1">{getStatusBadge(selectedSubmission.status)}</div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Submitted Data</h3>
                <SubmissionDetailContent submission={selectedSubmission} />
              </div>

              {selectedSubmission.reviewer_notes && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Reviewer Notes</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedSubmission.reviewer_notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedSubmission?.status === 'pending' && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleReject(selectedSubmission);
                  }}
                  disabled={processingAction}
                >
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button
                  onClick={() => handleApprove(selectedSubmission)}
                  disabled={processingAction}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {processingAction ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>
                  )}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <AlertDialog
        open={showApproveDialog}
        onOpenChange={(open) => {
          setShowApproveDialog(open);
          if (!open) setApprovingSubmission(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve submission?</AlertDialogTitle>
            <AlertDialogDescription>
              {approvingSubmission ? (
                <>
                  Approving will apply this {getTypeLabel(approvingSubmission.submission_type).toLowerCase()} entry to <strong>{approvingSubmission.student_name || 'this student'}</strong>'s record.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {approvingSubmission && (
            <div className="rounded border bg-gray-50 dark:bg-gray-800 p-3 text-sm space-y-1">
              <div className="flex items-center gap-2">
                {getTypeIcon(approvingSubmission.submission_type)}
                <span className="font-medium">{getTypeLabel(approvingSubmission.submission_type)}</span>
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                {approvingSubmission.entry_count || 0} {(approvingSubmission.entry_count || 0) === 1 ? 'entry' : 'entries'}
                {' · '}submitted {formatDate(approvingSubmission.created_at)}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingAction}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmApprove();
              }}
              disabled={processingAction}
              className="bg-green-600 hover:bg-green-700"
            >
              {processingAction ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Approving...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
            <DialogDescription>
              Provide a reason that will be visible to the student.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="reviewer-notes">Rejection Reason</Label>
              <Textarea
                id="reviewer-notes"
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                placeholder="e.g., The dates provided don't match our records. Please resubmit with corrected information."
                rows={4}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={processingAction}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitRejection}
              disabled={processingAction || !reviewerNotes.trim()}
            >
              {processingAction ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rejecting...</>
              ) : (
                <><XCircle className="w-4 h-4 mr-1" /> Reject Submission</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Export a helper for getting pending count (useful for sidebar badges)
export const fetchHistorySubmissionStats = async (token) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/admin/history-submissions/stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) return await response.json();
  } catch {
    // Silently fail for badge count
  }
  return { pending: 0, approved: 0, rejected: 0 };
};

export default HistorySubmissionReview;
